import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, FormControl, FormControlLabel, FormGroup, Checkbox,
  InputLabel, MenuItem, Paper, Select, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Tooltip, Typography, alpha, useTheme,
} from '@mui/material';
import { Add as AddIcon, Warehouse as WarehouseIcon } from '@mui/icons-material';
import PageHeader from '../../../../components/venue/PageHeader';
import { useVenue } from '../../../../context/VenueContext';
import { useAuth } from '../../../../context/AuthContext';
import warehouseService, {
  GRANTABLE_PERMISSIONS, IMPLIED_PERMISSIONS, WAREHOUSE_TYPES,
  type Warehouse, type WarehouseType, type EntitlementsGraph,
  type WarehousePermission,
} from '../../../../services/warehouseService';

/**
 * WAREHOUSE-ADMIN.1 — warehouses, grants, and who can reach what.
 *
 * The concept this screen has to make legible is OWNERSHIP: a warehouse either
 * belongs to one venue, or is shared across the company and reachable only by
 * grant. That distinction is taught by showing it in the data — three owned and
 * one shared in a single list — rather than by explaining it in prose.
 *
 * Deliberately avoided: the words "home cellar". "Home" is the schema's word,
 * and "cellar" is a TYPE here (a warehouse can be a kitchen or a bar store), so
 * using it for ownership would collide with the column next to it.
 */

const humanType = (t: WarehouseType) =>
  t.split('_').map(w => w[0] + w.slice(1).toLowerCase()).join(' ');

const WarehouseList: React.FC = () => {
  const theme = useTheme();
  const { venueId } = useVenue();
  /**
   * Creating warehouses and granting access are restricted to system admins for
   * now. Both are structural decisions about a COMPANY — who may sell whose
   * stock — rather than day-to-day venue operations, and both are effectively
   * permanent: ownership cannot be changed after creation, and a grant changes
   * what another venue's staff can sell. Everyone who can see the venue can
   * still READ the page; only the write affordances are gated.
   */
  const { isSystemAdmin } = useAuth();
  const canManage = isSystemAdmin();

  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [graph, setGraph] = useState<EntitlementsGraph | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState<WarehouseType>('CELLAR');
  // A real sentinel, not ''. MUI renders an empty string as nothing, so
  // choosing "Shared" left the field looking blank and unset.
  const SHARED = '__shared__';
  const [newOwner, setNewOwner] = useState<string>(SHARED);
  const [saving, setSaving] = useState(false);

  const [grantFor, setGrantFor] = useState<Warehouse | null>(null);
  const [grantVenue, setGrantVenue] = useState('');
  const [grantPerms, setGrantPerms] = useState<WarehousePermission[]>(['SELL']);

  const [locationFor, setLocationFor] = useState<Warehouse | null>(null);
  const [newLocationName, setNewLocationName] = useState('');

  /**
   * Deliberately does NOT clear the error.
   *
   * Every write handler reloads in its `finally`, so that a failed write still
   * refreshes the table — the alternative is an error like "already has a
   * location called General Stock" sitting above a row that reads "None".
   * Clearing here would then wipe the message it was called to explain, and the
   * write would appear to fail silently. Handlers clear it themselves before
   * they start.
   */
  const load = useCallback(async () => {
    if (!venueId) return;
    setLoading(true);
    try {
      const [list, ent] = await Promise.all([
        warehouseService.list(venueId),
        warehouseService.entitlements(venueId),
      ]);
      setWarehouses(list.warehouses);
      setGraph(ent);
    } catch (err: any) {
      setError(err?.response?.data?.error?.message ?? 'Failed to load warehouses.');
    } finally {
      setLoading(false);
    }
  }, [venueId]);

  useEffect(() => { load(); }, [load]);

  const companyVenues = graph?.venues ?? [];
  /**
   * Venues that could be granted a given warehouse: everyone in the company
   * except its owner, who already has full access.
   *
   * When this is empty there is nobody to share with — a company with one
   * inventory venue, which is every company on this system today. Offering
   * "Grant access" there opens a dialog with an empty picker and no way
   * forward, so the button is replaced by the reason.
   */
  const grantableFor = (w: Warehouse) =>
    companyVenues.filter(v => v.venueId !== w.homeVenueId);

  /**
   * A column in the reach grid is identified by its OWNER, not by its name.
   *
   * Every venue's home warehouse is auto-created as "Main Store", so a company
   * with four venues gets four columns reading the same two words and the grid
   * stops being readable — you cannot tell which "Main Store" a row is granting
   * access to. The name carries no information there; the owning venue always
   * does, and a venue has at most one home warehouse (the API rejects a second
   * with HOME_WAREHOUSE_EXISTS), so the venue name is a unique column label.
   *
   * The same rule the API already applies when it labels locations: where the
   * warehouse name says nothing, name the venue instead.
   *
   * A SHARED warehouse keeps its own name. Nobody owns it, so there is no venue
   * to name it by — and the name was typed by a person rather than generated,
   * which makes it the informative half already.
   */
  const venueNameById = new Map(companyVenues.map(v => [v.venueId, v.venueName]));

  const columnLabel = (w: EntitlementsGraph['warehouses'][number]) => {
    if (w.isShared) return { primary: w.name, secondary: 'Shared' };
    const owner = w.homeVenueId ? venueNameById.get(w.homeVenueId) ?? null : null;
    // No owner name means the home venue is missing from the company list —
    // fall back to the warehouse's own name rather than render an empty header.
    return owner
      ? { primary: owner, secondary: `${w.name} · Owned` }
      : { primary: w.name, secondary: 'Owned' };
  };

  const handleCreate = async () => {
    if (!venueId || !newName.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await warehouseService.create(venueId, {
        name: newName.trim(),
        type: newType,
        // Translated at the boundary: the API models shared as a null owner.
        homeVenueId: newOwner === SHARED ? null : newOwner,
      });
      setCreateOpen(false);
      setNewName(''); setNewType('CELLAR'); setNewOwner(SHARED);
    } catch (err: any) {
      setError(err?.response?.data?.error?.message ?? 'Failed to create warehouse.');
    } finally {
      setSaving(false);
      // See handleAddLocation: an error must not leave a stale table behind it.
      await load();
    }
  };

  const handleGrant = async () => {
    if (!venueId || !grantFor || !grantVenue) return;
    setSaving(true);
    setError(null);
    try {
      await warehouseService.setGrant(venueId, grantFor._id, {
        granteeId: grantVenue,
        // VIEW is implied rather than chosen: anything you may do here, you may see.
        permissions: Array.from(new Set([...grantPerms, ...IMPLIED_PERMISSIONS])),
      });
      setGrantFor(null); setGrantVenue(''); setGrantPerms(['SELL']);
    } catch (err: any) {
      setError(err?.response?.data?.error?.message ?? 'Failed to save grant.');
    } finally {
      setSaving(false);
      await load();
    }
  };

  const handleRevoke = async (w: Warehouse, grantId: string) => {
    if (!venueId) return;
    setError(null);
    try {
      await warehouseService.revokeGrant(venueId, w._id, grantId);
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.error?.message ?? 'Failed to revoke grant.');
    }
  };

  const handleAddLocation = async () => {
    if (!venueId || !locationFor || !newLocationName.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await warehouseService.addLocation(venueId, locationFor._id, { name: newLocationName.trim() });
      setLocationFor(null); setNewLocationName('');
    } catch (err: any) {
      setError(err?.response?.data?.error?.message ?? 'Failed to add location.');
    } finally {
      setSaving(false);
      // Reloaded even on failure, because the most confusing error this screen
      // can show is "already has a location called X" above a row that still
      // reads "None": the name was taken by a request that SUCCEEDED, and
      // leaving the pre-add snapshot on screen makes a correct message look
      // like a bug in the system rather than a duplicate.
      await load();
    }
  };

  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <PageHeader
        icon={<WarehouseIcon />}
        title="Warehouses"
        count={warehouses.length}
        subtitle="Where your company holds stock. A warehouse belongs to one venue, or is shared — and a venue always reaches its own, which cannot be taken away."
        actions={canManage ? (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateOpen(true)}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            New Warehouse
          </Button>
        ) : undefined}
      />

      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

      {/* ── The warehouses themselves ─────────────────────────────────────── */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        <TableContainer sx={{ overflowX: 'auto' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Ownership</TableCell>
              <TableCell>Locations</TableCell>
              <TableCell>Venues with access</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={6}>
                  <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
                    <CircularProgress size={32} />
                  </Box>
                </TableCell>
              </TableRow>
            )}
            {!loading && warehouses.length === 0 && (
              <TableRow>
                <TableCell colSpan={6}>
                  <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      No warehouses yet.
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
            {warehouses.map(w => (
              <TableRow key={w._id} hover>
                <TableCell><Typography fontWeight={500}>{w.name}</Typography></TableCell>
                <TableCell>{humanType(w.type)}</TableCell>
                <TableCell>
                  {/* The teaching moment: ownership shown as data, side by side
                      with shared, so the distinction reads at a glance. */}
                  {w.isShared ? (
                    <Chip
                      label="Shared"
                      size="small"
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.primary.main,
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                      }}
                    />
                  ) : (
                    <Typography variant="body2">Owned by {w.homeVenueName ?? '—'}</Typography>
                  )}
                </TableCell>
                <TableCell>
                  {/* Stock lives at a LOCATION, not at the warehouse — so a
                      warehouse without one holds nothing and cannot be sold
                      from. Saying that plainly here is the difference between
                      an empty cell and an explanation. */}
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap alignItems="center">
                    {w.locations.length === 0 ? (
                      <Typography variant="body2" color="warning.main">
                        None — cannot hold stock
                      </Typography>
                    ) : (
                      w.locations.map(l => (
                        <Chip key={l._id} size="small" variant="outlined" label={l.name} />
                      ))
                    )}
                    {canManage && (
                      <Button size="small" onClick={() => setLocationFor(w)}>Add</Button>
                    )}
                  </Stack>
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    {/* The owner is listed first and has no delete — its access
                        is implicit and cannot be revoked, so offering a control
                        that then errors would be the UI's fault. */}
                    {!w.isShared && (
                      <Chip size="small" variant="outlined" label={`${w.homeVenueName} (owner)`} />
                    )}
                    {w.grants.map(g => (
                      <Chip
                        key={g._id}
                        size="small"
                        label={`${g.granteeName ?? g.granteeId} · ${g.permissions.join(', ')}`}
                        onDelete={canManage ? () => handleRevoke(w, g._id) : undefined}
                      />
                    ))}
                    {/* A shared warehouse has no owner, so EVERY company venue
                        is grantable — there is always someone to grant to, and
                        no "nobody available" state to write copy for. */}
                    {w.grants.length === 0 && w.isShared && (
                      <Typography variant="body2" color="text.secondary">
                        Nobody yet — grant a venue to make this usable
                      </Typography>
                    )}
                  </Stack>
                </TableCell>
                <TableCell align="right">
                  {!canManage ? null : grantableFor(w).length > 0 ? (
                    <Button
                      size="small"
                      onClick={() => setGrantFor(w)}
                      sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                      Grant access
                    </Button>
                  ) : (
                    <Tooltip title="Sharing needs a second venue in this company that uses inventory.">
                      <Typography variant="body2" color="text.disabled">
                        No one to share with
                      </Typography>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </TableContainer>
      </Paper>

      {/* ── Who can reach what ────────────────────────────────────────────── */}
      <PageHeader
        title="Who can reach what"
        subtitle="Filled means the venue owns it. Outlined means access was granted and can be revoked."
      />

      <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        <TableContainer sx={{ overflowX: 'auto' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Venue</TableCell>
              {(graph?.warehouses ?? []).map(w => {
                const label = columnLabel(w);
                return (
                  <TableCell key={w._id} align="center">
                    <Typography variant="body2" fontWeight={500}>{label.primary}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {label.secondary}
                    </Typography>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {companyVenues.map(v => (
              <TableRow key={v.venueId} hover>
                <TableCell><Typography variant="body2">{v.venueName}</Typography></TableCell>
                {(graph?.warehouses ?? []).map(w => {
                  const reach = v.reaches.find(r => r.warehouseId === w._id);
                  if (!reach) {
                    return (
                      <TableCell key={w._id} align="center">
                        <Typography variant="body2" color="text.disabled">—</Typography>
                      </TableCell>
                    );
                  }
                  return (
                    <TableCell key={w._id} align="center">
                      <Tooltip title={reach.permissions.join(', ')}>
                        <Chip
                          size="small"
                          label={reach.isHome ? 'Owns' : reach.permissions.join(', ')}
                          variant={reach.isHome ? 'filled' : 'outlined'}
                          sx={reach.isHome ? {
                            fontWeight: 600,
                            color: theme.palette.success.dark,
                            backgroundColor: alpha(theme.palette.success.main, 0.14),
                          } : undefined}
                        />
                      </Tooltip>
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </TableContainer>
      </Paper>

      {/* ── Create ────────────────────────────────────────────────────────── */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New warehouse</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <TextField
              label="Name" fullWidth autoFocus value={newName}
              onChange={e => setNewName(e.target.value)}
            />
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select value={newType} label="Type" onChange={e => setNewType(e.target.value as WarehouseType)}>
                {WAREHOUSE_TYPES.map(t => <MenuItem key={t} value={t}>{humanType(t)}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Ownership</InputLabel>
              <Select value={newOwner} label="Ownership" onChange={e => setNewOwner(e.target.value)}>
                <MenuItem value={SHARED}>Shared across the company</MenuItem>
                {companyVenues.map(v => (
                  <MenuItem key={v.venueId} value={v.venueId}>Owned by {v.venueName}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {/* Stated because it is permanent — the fix for getting it wrong is
                creating another warehouse and transferring stock. */}
            <DialogContentText variant="body2">
              {newOwner === SHARED
                ? 'A shared warehouse belongs to the company. Venues reach it only by grant.'
                : 'That venue will always have full access to this warehouse, and it cannot be revoked.'}
              {' '}Ownership cannot be changed later.
            </DialogContentText>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={saving || !newName.trim()} onClick={handleCreate}>
            {saving ? 'Creating…' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Grant ─────────────────────────────────────────────────────────── */}
      <Dialog open={!!grantFor} onClose={() => setGrantFor(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Grant access to {grantFor?.name}</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Venue</InputLabel>
              <Select value={grantVenue} label="Venue" onChange={e => setGrantVenue(e.target.value)}>
                {/* The owner already has full access, so offering it here and
                    then rejecting the save would be the UI's mistake. */}
                {(grantFor ? grantableFor(grantFor) : [])
                  .map(v => <MenuItem key={v.venueId} value={v.venueId}>{v.venueName}</MenuItem>)}
              </Select>
            </FormControl>
            {/* Every box here decides something — the write paths now resolve
                their own permission. ADJUST and VIEW_COST are still enforced
                nowhere and are deliberately absent. */}
            <FormGroup>
              {GRANTABLE_PERMISSIONS.map(p => (
                <FormControlLabel
                  key={p.value}
                  control={
                    <Checkbox
                      checked={grantPerms.includes(p.value)}
                      onChange={e => setGrantPerms(prev =>
                        e.target.checked
                          ? [...prev, p.value]
                          : prev.filter(x => x !== p.value))}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2">{p.label}</Typography>
                      <Typography variant="caption" color="text.secondary">{p.help}</Typography>
                    </Box>
                  }
                />
              ))}
            </FormGroup>
            {/* A shared cellar has no owner to fall back on, so somebody must
                be able to put stock in it or it stays empty forever. Worth
                saying at the moment the decision is being made. */}
            {grantFor?.isShared && !grantPerms.includes('RECEIVE') && (
              <DialogContentText variant="body2" color="warning.main">
                Nobody can put stock into {grantFor?.name} yet. It is shared, so it has
                no owner to receive on its behalf — grant <strong>Receive</strong> to at
                least one venue.
              </DialogContentText>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGrantFor(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={saving || !grantVenue || grantPerms.length === 0}
            onClick={handleGrant}
          >
            {saving ? 'Saving…' : 'Grant'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Add a location ─────────────────────────────────────────────────── */}
      <Dialog open={!!locationFor} onClose={() => setLocationFor(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Add a location to {locationFor?.name}</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <TextField
              label="Location name"
              value={newLocationName}
              onChange={e => setNewLocationName(e.target.value)}
              fullWidth
              autoFocus
              placeholder="Rack 3"
            />
            {/* The concept worth teaching at exactly this moment: the warehouse
                is the organisational unit, the location is the physical place,
                and stock is only ever counted at the latter. */}
            <DialogContentText variant="body2">
              Stock is held at a location, not at the warehouse itself — so this is
              the shelf, rack or room a bottle actually sits on. Counts, transfers
              and sales all happen against it.
            </DialogContentText>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLocationFor(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={saving || !newLocationName.trim()}
            onClick={handleAddLocation}
          >
            {saving ? 'Saving…' : 'Add location'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WarehouseList;
